/**
 * Coupons Management Page — شركة المياه
 * =======================================
 * Phase 1 — Simple, focused coupon management.
 * Features: Create / Edit / Toggle / Delete coupons with ship scoping.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import SAR from '../../../components/SAR';
import { useDialog } from '../../../context/DialogContext';

// ── Icons ──────────────────────────────────────────────────────────────
const I = {
  search:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  tag:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  copy:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  refresh:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  x:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  ship:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 21c.6.5 1.2 1 2.5 1C7 22 7 20 9.5 20s2.5 2 5 2 2.5-2 5-2c1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.61 7.44"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><polyline points="12 5 12 9"/></svg>,
  check:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  toggle:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>,
};

// ── Status badge config ────────────────────────────────────────────────
const STATUS_MAP = {
  active:    { label: 'نشط',        color: '#10b981', bg: '#ecfdf5' },
  expired:   { label: 'منتهي',      color: '#ef4444', bg: '#fef2f2' },
  inactive:  { label: 'معطّل',      color: '#6b7280', bg: '#f9fafb' },
  exhausted: { label: 'مستنفد',     color: '#f59e0b', bg: '#fffbeb' },
};

const StatusBadge = ({ label }) => {
  const s = STATUS_MAP[label] || STATUS_MAP.inactive;
  return (
    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                   color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

// ── Stat card ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, color, onClick, active }) => (
  <button onClick={onClick} style={{
    flex: '1 1 140px', minWidth: '120px', padding: '16px 20px', borderRadius: '12px',
    background: active ? color + '15' : 'var(--tenant-surface,#fff)',
    border: `1.5px solid ${active ? color : 'var(--tenant-border,#e2e8f0)'}`,
    cursor: 'pointer', textAlign: 'right', transition: 'all .2s',
  }}>
    <div style={{ fontSize: '24px', fontWeight: '700', color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ fontSize: '12px', color: 'var(--tenant-text-muted,#64748b)', marginTop: '4px' }}>{label}</div>
  </button>
);

// ── Main component ─────────────────────────────────────────────────────
const PromotionsPage = () => {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [coupons, setCoupons]   = useState([]);
  const [stats, setStats]       = useState({});
  const [ships, setShips]       = useState([]);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [codeStatus, setCodeStatus] = useState(null); // null | 'checking' | 'ok' | 'taken'
  const codeCheckTimer = useRef(null);

  const EMPTY_FORM = {
    code: '', discount_type: 'percentage', discount_value: '',
    max_discount_amount: '', min_order_amount: '',
    expires_at: '', max_redemptions: '', max_redemptions_per_customer: '1',
    applies_to: 'all', ship_ids: [],
  };
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Load data ──────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter !== 'all') params.set('status', filter);
      if (search)          params.set('search', search);
      const [cData, sData, shData] = await Promise.all([
        apiCall(`/tenant/promotions/coupons?${params}`),
        apiCall('/tenant/promotions/coupons/stats'),
        apiCall('/tenant/ships?limit=200'),
      ]);
      setCoupons(cData.items || []);
      setStats(sData || {});
      setShips(shData.ships || shData.items || []);
    } catch { showError('فشل تحميل البيانات'); }
    finally  { setLoading(false); }
  }, [apiCall, showError, filter, search]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Code availability check (debounced) ───────────────────────────
  const handleCodeChange = (val) => {
    const code = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
    setForm(f => ({ ...f, code }));
    setCodeStatus(null);
    if (codeCheckTimer.current) clearTimeout(codeCheckTimer.current);
    if (code.length === 5 && !editing) {
      setCodeStatus('checking');
      codeCheckTimer.current = setTimeout(async () => {
        try {
          const res = await apiCall(`/tenant/promotions/coupons/check-code?code=${code}`);
          setCodeStatus(res.available ? 'ok' : 'taken');
        } catch { setCodeStatus(null); }
      }, 400);
    }
  };

  const generateCode = async () => {
    try {
      const res = await apiCall('/tenant/promotions/coupons/generate-code');
      setForm(f => ({ ...f, code: res.code }));
      setCodeStatus('ok');
    } catch { showError('فشل توليد الكود'); }
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && codeStatus === 'taken') { showError('هذا الكود مستخدم بالفعل'); return; }
    if (form.code.length !== 5) { showError('الكود يجب أن يكون 5 أحرف بالضبط'); return; }
    if (form.applies_to === 'ships' && form.ship_ids.length === 0) {
      showError('اختر محطة واحدة على الأقل'); return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code, discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value) || 0,
        max_discount_amount: form.max_discount_amount ? parseFloat(form.max_discount_amount) : null,
        min_order_amount:    form.min_order_amount    ? parseFloat(form.min_order_amount)    : null,
        expires_at:          form.expires_at          ? new Date(form.expires_at).toISOString() : null,
        max_redemptions:     form.max_redemptions     ? parseInt(form.max_redemptions)          : null,
        max_redemptions_per_customer: parseInt(form.max_redemptions_per_customer) || 1,
        applies_to: form.applies_to, ship_ids: form.ship_ids,
      };
      if (editing) {
        await apiCall(`/tenant/promotions/coupons/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        success('تم تحديث الكوبون بنجاح');
      } else {
        await apiCall('/tenant/promotions/coupons', { method: 'POST', body: JSON.stringify(payload) });
        success('تم إنشاء الكوبون بنجاح');
      }
      closeModal(); loadAll();
    } catch (err) { showError(err.message || 'فشلت العملية'); }
    finally { setSaving(false); }
  };

  // ── Toggle ─────────────────────────────────────────────────────────
  const toggleCoupon = async (c) => {
    try {
      const res = await apiCall(`/tenant/promotions/coupons/${c.id}/toggle`, { method: 'PATCH' });
      success(res.message || 'تم تحديث الحالة');
      loadAll();
    } catch { showError('فشل تحديث الحالة'); }
  };

  // ── Delete ─────────────────────────────────────────────────────────
  const deleteCoupon = async (c) => {
    if (!await confirm({ title: 'إيقاف الكوبون', message: `هل تريد إيقاف الكوبون "${c.code}" نهائياً؟`, type: 'delete', confirmText: 'إيقاف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/promotions/coupons/${c.id}`, { method: 'DELETE' });
      success('تم إيقاف الكوبون');
      loadAll();
    } catch (err) { showError(err.message || 'فشل الإيقاف'); }
  };

  // ── Copy code ──────────────────────────────────────────────────────
  const copyCode = (code) => { navigator.clipboard.writeText(code).then(() => success('تم نسخ الكود')); };

  // ── Edit open ──────────────────────────────────────────────────────
  const openEdit = async (c) => {
    setEditing(c);
    setCodeStatus(null);
    try {
      const detail = await apiCall(`/tenant/promotions/coupons/${c.id}`);
      setForm({
        code: detail.code, discount_type: detail.discount_type || 'percentage',
        discount_value: String(detail.discount_value || ''),
        max_discount_amount: detail.max_discount_amount ? String(detail.max_discount_amount) : '',
        min_order_amount: detail.min_order_amount ? String(detail.min_order_amount) : '',
        expires_at: detail.expires_at ? detail.expires_at.split('T')[0] : '',
        max_redemptions: detail.max_redemptions ? String(detail.max_redemptions) : '',
        max_redemptions_per_customer: String(detail.max_redemptions_per_customer || 1),
        applies_to: detail.applies_to || 'all',
        ship_ids: detail.ship_ids || [],
      });
    } catch { setForm({ ...EMPTY_FORM, ...c }); }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); setForm(EMPTY_FORM); setCodeStatus(null); };

  const toggleShip = (sid) =>
    setForm(f => ({ ...f, ship_ids: f.ship_ids.includes(sid) ? f.ship_ids.filter(x => x !== sid) : [...f.ship_ids, sid] }));

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '—';
  const fmtDiscount = (c) => c.discount_type === 'percentage'
    ? `${c.discount_value}%`
    : <>{c.discount_value} <SAR className="h-[0.75em] w-auto" /></>;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة الكوبونات</h1>
          <p>أنشئ وأدر كوبونات الخصم للعملاء</p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setCodeStatus(null); setShowModal(true); }}>
          {I.plus} <span>كوبون جديد</span>
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <StatCard label="الكل"        value={stats.total || 0}         color="#6366f1" onClick={() => setFilter('all')}       active={filter === 'all'} />
        <StatCard label="نشط"         value={stats.active || 0}        color="#10b981" onClick={() => setFilter('active')}    active={filter === 'active'} />
        <StatCard label="منتهي"       value={stats.expired || 0}       color="#ef4444" onClick={() => setFilter('expired')}   active={filter === 'expired'} />
        <StatCard label="معطّل"       value={stats.inactive || 0}      color="#6b7280" onClick={() => setFilter('inactive')}  active={filter === 'inactive'} />
        <StatCard label="مستنفد"      value={stats.exhausted || 0}     color="#f59e0b" onClick={() => setFilter('exhausted')} active={filter === 'exhausted'} />
        <StatCard label="إجمالي الاستخدامات" value={stats.total_uses || 0}  color="#3b82f6" onClick={() => {}} active={false} />
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative', maxWidth: '340px' }}>
          <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', width: '18px', height: '18px' }}>{I.search}</span>
          <input
            type="text" placeholder="ابحث بالكود…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid var(--tenant-border,#e2e8f0)', borderRadius: '10px', fontSize: '14px', outline: 'none', background: 'var(--tenant-surface,#fff)' }}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : coupons.length === 0 ? (
        <div className="page-empty">
          <div style={{ width: '48px', height: '48px', color: '#cbd5e1', marginBottom: '12px' }}>{I.tag}</div>
          <h3>لا توجد كوبونات</h3>
          <p>{filter !== 'all' ? 'لا يوجد كوبونات بهذه الحالة' : 'ابدأ بإنشاء كوبون جديد'}</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>الكود</th>
                <th>الخصم</th>
                <th>الحد الأدنى</th>
                <th>الاستخدام</th>
                <th>النطاق</th>
                <th>الانتهاء</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '15px', letterSpacing: '2px', color: 'var(--tenant-primary,#2563eb)' }}>{c.code}</span>
                      <button onClick={() => copyCode(c.code)} title="نسخ" style={{ width: '24px', height: '24px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.copy}</button>
                    </div>
                  </td>
                  <td style={{ fontWeight: '600' }}>
                    {fmtDiscount(c)}
                    {c.max_discount_amount && c.discount_type === 'percentage' &&
                      <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>بحد أقصى {c.max_discount_amount} <SAR className="h-[0.7em] w-auto" /></span>}
                  </td>
                  <td>{c.min_order_amount ? <>{c.min_order_amount} <SAR className="h-[0.75em] w-auto" /></> : '—'}</td>
                  <td>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.redemption_count}</span>
                    {c.max_redemptions && <span style={{ color: '#94a3b8' }}> / {c.max_redemptions}</span>}
                  </td>
                  <td>
                    {c.applies_to === 'ships'
                      ? <span style={{ fontSize: '12px', color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: '10px' }}>أشياب محددة ({c.ship_count})</span>
                      : <span style={{ fontSize: '12px', color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: '10px' }}>كل الأشياب</span>}
                  </td>
                  <td style={{ fontSize: '13px', color: c.status_label === 'expired' ? '#ef4444' : 'inherit' }}>{fmtDate(c.expires_at)}</td>
                  <td><StatusBadge label={c.status_label} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button className="action-btn" onClick={() => openEdit(c)} title="تعديل">{I.edit}</button>
                      <button
                        className={`action-btn ${c.is_active ? '' : 'action-btn--success'}`}
                        onClick={() => toggleCoupon(c)}
                        title={c.is_active ? 'تعطيل' : 'تفعيل'}
                        style={{ color: c.is_active ? '#6b7280' : '#10b981' }}
                      >{I.toggle}</button>
                      <button className="action-btn action-btn--danger" onClick={() => deleteCoupon(c)} title="إيقاف">{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: '560px', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'تعديل كوبون' : 'كوبون جديد'}</h2>
              <button className="modal-close" onClick={closeModal}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>

                  {/* Code */}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>كود الكوبون <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input
                          type="text" maxLength={5} required
                          value={form.code}
                          onChange={e => handleCodeChange(e.target.value)}
                          disabled={!!editing}
                          placeholder="5 أحرف / أرقام"
                          style={{ width: '100%', fontFamily: 'monospace', fontWeight: '700', fontSize: '18px', letterSpacing: '4px', textAlign: 'center', textTransform: 'uppercase', padding: '10px', border: `1.5px solid ${codeStatus === 'ok' ? '#10b981' : codeStatus === 'taken' ? '#ef4444' : '#e2e8f0'}`, borderRadius: '10px' }}
                        />
                        {codeStatus === 'checking' && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#94a3b8' }}>جاري التحقق…</span>}
                        {codeStatus === 'ok'       && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }}>{I.check}</span>}
                        {codeStatus === 'taken'    && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444', fontSize: '12px' }}>مستخدم</span>}
                      </div>
                      {!editing && (
                        <button type="button" onClick={generateCode} style={{ padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', background: 'var(--tenant-surface,#fff)', cursor: 'pointer', display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          <span style={{ width: '16px', height: '16px' }}>{I.refresh}</span> توليد تلقائي
                        </button>
                      )}
                    </div>
                    {codeStatus === 'taken' && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>هذا الكود مستخدم في المنصة. اختر كوداً آخر.</p>}
                  </div>

                  {/* Discount type */}
                  <div className="form-group">
                    <label>نوع الخصم</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[{ v: 'percentage', l: <span>نسبة مئوية (%)</span> }, { v: 'fixed_amount', l: <span>مبلغ ثابت (<SAR style={{ width: '0.85em', height: '0.85em' }} />)</span> }].map(opt => (
                        <button key={opt.v} type="button"
                          onClick={() => setForm(f => ({ ...f, discount_type: opt.v, max_discount_amount: '' }))}
                          style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: `1.5px solid ${form.discount_type === opt.v ? 'var(--tenant-primary,#2563eb)' : '#e2e8f0'}`, background: form.discount_type === opt.v ? 'var(--tenant-primary-light,#eff6ff)' : '#fff', color: form.discount_type === opt.v ? 'var(--tenant-primary,#2563eb)' : 'inherit', fontWeight: form.discount_type === opt.v ? '600' : '400', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Discount value */}
                  <div className="form-group">
                    <label>قيمة الخصم <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="number" required min="0.01" max={form.discount_type === 'percentage' ? 100 : undefined} step="0.01"
                      value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                      placeholder={form.discount_type === 'percentage' ? '0 - 100' : 'مثال: 50'} />
                  </div>

                  {/* Max discount cap (only for percentage) */}
                  {form.discount_type === 'percentage' && (
                    <div className="form-group">
                      <label>الحد الأقصى للخصم (اختياري)</label>
                      <input type="number" min="0" step="0.01" value={form.max_discount_amount}
                        onChange={e => setForm(f => ({ ...f, max_discount_amount: e.target.value }))}
                        placeholder="مثال: 100" />
                    </div>
                  )}

                  {/* Min order */}
                  <div className="form-group">
                    <label>الحد الأدنى للطلب (اختياري)</label>
                    <input type="number" min="0" step="0.01" value={form.min_order_amount}
                      onChange={e => setForm(f => ({ ...f, min_order_amount: e.target.value }))}
                      placeholder="مثال: 100" />
                  </div>

                  {/* Expires at */}
                  <div className="form-group">
                    <label>تاريخ الانتهاء (اختياري)</label>
                    <input type="date" value={form.expires_at} min={new Date().toISOString().split('T')[0]}
                      onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
                  </div>

                  {/* Max total uses */}
                  <div className="form-group">
                    <label>الحد الأقصى للاستخدامات الكلية</label>
                    <input type="number" min="1" value={form.max_redemptions}
                      onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                      placeholder="فارغ = غير محدود" />
                  </div>

                  {/* Per customer limit */}
                  <div className="form-group">
                    <label>الحد لكل عميل</label>
                    <select value={form.max_redemptions_per_customer}
                      onChange={e => setForm(f => ({ ...f, max_redemptions_per_customer: e.target.value }))}>
                      <option value="1">مرة واحدة فقط</option>
                      <option value="2">مرتين</option>
                      <option value="3">3 مرات</option>
                      <option value="5">5 مرات</option>
                      <option value="0">غير محدود</option>
                    </select>
                  </div>

                  {/* Scope */}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>نطاق التطبيق</label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {[{ val: 'all', label: 'كل أشيابي' }, { val: 'ships', label: 'أشياب محددة' }].map(opt => (
                        <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 16px', border: `1.5px solid ${form.applies_to === opt.val ? 'var(--tenant-primary,#2563eb)' : '#e2e8f0'}`, borderRadius: '10px', background: form.applies_to === opt.val ? 'var(--tenant-primary-light,#eff6ff)' : 'transparent' }}>
                          <input type="radio" name="applies_to" value={opt.val} checked={form.applies_to === opt.val} onChange={() => setForm(f => ({ ...f, applies_to: opt.val, ship_ids: [] }))} style={{ accentColor: 'var(--tenant-primary,#2563eb)' }} />
                          <div style={{ width: '18px', height: '18px', color: form.applies_to === opt.val ? 'var(--tenant-primary,#2563eb)' : '#94a3b8' }}>{I.ship}</div>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Ship multi-select */}
                  {form.applies_to === 'ships' && (
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                      <label>اختر الأشياب <span style={{ color: '#ef4444' }}>*</span></label>
                      {ships.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: '13px' }}>لا توجد أشياب مسجّلة</p>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '200px', overflowY: 'auto', padding: '4px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                          {ships.map(s => {
                            const selected = form.ship_ids.includes(s.id);
                            return (
                              <button key={s.id} type="button" onClick={() => toggleShip(s.id)}
                                style={{ padding: '6px 14px', borderRadius: '20px', border: `1.5px solid ${selected ? 'var(--tenant-primary,#2563eb)' : '#e2e8f0'}`, background: selected ? 'var(--tenant-primary-light,#eff6ff)' : 'transparent', cursor: 'pointer', fontSize: '13px', color: selected ? 'var(--tenant-primary,#2563eb)' : 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {selected && <span style={{ width: '14px', height: '14px' }}>{I.check}</span>}
                                {s.name_ar || s.name}
                                {s.city && <span style={{ color: '#94a3b8', fontSize: '11px' }}>({s.city})</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {form.ship_ids.length > 0 && (
                        <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>تم اختيار {form.ship_ids.length} شيب</p>
                      )}
                    </div>
                  )}

                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={closeModal}>إلغاء</button>
                <button type="submit" className="btn btn--primary" disabled={saving || codeStatus === 'taken'}>
                  {saving ? 'جاري الحفظ…' : editing ? 'حفظ التعديلات' : 'إنشاء الكوبون'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionsPage;
